import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import PageShell from "../components/PageShell";
import { SparklesIcon } from "../components/Icons";
import { useAuth } from "../context/AuthContext";
import "./Memories.css";

// Memories.js - Shared Team Knowledge Component
// Implements the "Mutual High-Five" handshake flow.
// Maddie logs a win -> Sam gets a "Pending Ping" -> Sam Approves -> Becomes Shared memory.

const Memories = () => {
  const { user } = useAuth();
  
  // State for data management
  const [memories, setMemories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtering & UI state
  const location = useLocation();
  const querySpace = new URLSearchParams(location.search).get("space");
  const [space, setSpace] = useState(querySpace || "all"); // Filter: all, personal, shared, pending
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    setSpace(querySpace || "all");
  }, [querySpace]);

  // Editing the existing memory
  const [editingMemory, setEditingMemory] = useState(null);

  // Form state for creation
  const [newMemory, setNewMemory] = useState({ 
    title: "", 
    content: "", 
    tags: "", 
    visibility: "private",
    recipient_id: "" 
  });

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get("/api/employees");
      // Filter out the current user from the recipient list
      setEmployees(res.data.filter(e => e.id !== user?.id));
    } catch (err) {
      console.error("Failed to fetch employees", err);
    }
  }, [user?.id]);

  const fetchMemories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/memories?space=${space}&search=${search}&status=${space === 'pending' ? 'pending' : 'approved'}`);
      setMemories(res.data);
    } catch (err) {
      console.error("Failed to fetch memories", err);
    } finally {
      setLoading(false);
    }
  }, [search, space]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleAddMemory = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newMemory,
        tags: newMemory.tags.split(",").map(t => t.trim()).filter(t => t)
      };
      await axios.post("/api/memories", payload);
      setShowAddModal(false);
      setNewMemory({ title: "", content: "", tags: "", visibility: "private", recipient_id: "" });
      fetchMemories();
    } catch (err) {
      alert("Failed to create memory");
    }
  };

  const handleHandshake = async (memoryId, action) => {
    try {
      await axios.post(`/api/memories/${memoryId}/approve`, { action });
      fetchMemories();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to process handshake");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this memory?")) return;
    try {
      await axios.delete(`/api/memories/${id}`);
      fetchMemories();
    } catch (err) {
      alert("Failed to delete memory");
    }
  };

    // handler for edit and save 
  const handleEditSave = async (e) => {
  e.preventDefault();
  try {
    const payload = {
      title: editingMemory.title,
      content: editingMemory.content,
      tags: typeof editingMemory.tags === "string"
        ? editingMemory.tags.split(",").map(t => t.trim()).filter(t => t)
        : editingMemory.tags,
    };
    await axios.put(`/api/memories/${editingMemory.id}`, payload);
    setEditingMemory(null);
    fetchMemories();
  } catch (err) {
    alert("Failed to update memory");
  }
};

  // commented out the toggleVisiblity funtionality: diasbled the funtionality of  make it private or share 

  // const toggleVisibility = async (memory) => {
  //   const newVisibility = memory.visibility === "private" ? "shared" : "private";
  //   try {
  //     await axios.put(`/api/memories/${memory.id}`, { visibility: newVisibility });
  //     fetchMemories();
  //   } catch (err) {
  //     alert("Failed to update visibility");
  //   }
  // };

  return (
    <PageShell
      title="Shared Memories"
      subtitle="The Vault: Only approved shared moments are used for AI greetings."
      actions={
        user?.role !== "company_admin" && (
          <button className="btn btn--primary" onClick={() => setShowAddModal(true)}>
            <SparklesIcon className="icon" />
            <span>Log a Moment</span>
          </button>
        )
      }
    >
      <div className="memories-header">
        <div className="space-toggle">
          <button
            className={`toggle-btn ${space === "all" ? "active" : ""}`}
            onClick={() => setSpace("all")}
          >All</button>
          <button
            className={`toggle-btn ${space === "personal" ? "active" : ""}`}
            onClick={() => setSpace("personal")}
          >Personal</button>
          <button
            className={`toggle-btn ${space === "shared" ? "active" : ""}`}
            onClick={() => setSpace("shared")}
          >Mutual Wins</button>
          <button
            className={`toggle-btn ${space === "pending" ? "active" : ""}`}
            onClick={() => setSpace("pending")}
          >Pending Pings</button>
        </div>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search titles, content, or tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading your vault...</div>
      ) : (
        <div className="memories-grid">
          {memories.length > 0 ? (
            memories.map(m => (
              <div key={m.id} className={`memory-card ${m.visibility} ${m.status}`}>
                <div className="memory-card__header">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className={`visibility-badge ${m.visibility} ${m.status}`}>
                      {m.status === 'pending' ? 'Pending Approval' : m.visibility}
                    </span>
                  {m.visibility === "shared" && (
                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                      with {m.creator_id === user?.id ? m.recipient_name : m.creator_name}
                    </span>
                  )}
                  </div>
                  <div className="memory-card__actions">
                    
{/*                     
                    removed make it private or share toggle button
                    {m.creator_id === user?.id && (
                      <button onClick={() => toggleVisibility(m)} title="Toggle Visibility">
                        {m.visibility === "private" ? "Share" : "Make Private"}
                      </button>
                    )} */}
                    {m.creator_id === user?.id && (
                      <button
                        className="edit-btn"
                        onClick={() => setEditingMemory({ ...m, tags: m.tags?.join(", ") || "" })}
                      >
                        Edit
                      </button>
                    )}

                    
                    <button className="delete-btn" onClick={() => handleDelete(m.id)}>×</button>
                  </div>
                </div>
                
                <h3 className="memory-card__title">{m.title}</h3>
                
                {m.status === 'pending' && m.recipient_id === user?.id && (
                  <div className="handshake-status">
                    Maddie requested a "Mutual High-Five" on this moment.
                  </div>
                )}
                
                <p className="memory-card__content">{m.content}</p>
                
                <div className="memory-card__tags">
                  {m.tags.map(tag => (
                    <span key={tag} className="tag" onClick={() => setSearch(tag)}>#{tag}</span>
                  ))}
                </div>

                {m.status === 'pending' && m.recipient_id === user?.id && (
                  <div className="handshake-actions">
                    <button className="btn btn--primary btn--sm" onClick={() => handleHandshake(m.id, 'approved')}>
                      Approve & Vault
                    </button>
                    <button className="btn btn--ghost btn--sm" onClick={() => handleHandshake(m.id, 'declined')}>
                      Decline
                    </button>
                  </div>
                )}

                {m.status === 'pending' && m.creator_id === user?.id && (
                  <div className="handshake-status">
                    Verification pending from recipient...
                  </div>
                )}

                <div className="memory-card__footer">
                  <span className="creator">By {m.creator_name}</span>
                  <span className="date">{new Date(m.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <img src="/images/empty_memories.png" alt="Empty Vault" />
              <p>Your team's vault is currently quiet. Log a shared moment to start building your collective memory.</p>
              {user?.role !== "company_admin" && (
                <button className="btn btn--primary" onClick={() => setShowAddModal(true)}>Log your first moment</button>
              )}
            </div>
          )}
        </div>
      )}

      {showAddModal && user?.role !== "company_admin" && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal__header">
              <h2>Log a Moment</h2>
              <button onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddMemory}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  required
                  value={newMemory.title}
                  onChange={e => setNewMemory({ ...newMemory, title: e.target.value })}
                  placeholder="What happened?"
                />
              </div>
              <div className="form-group">
                <label>The Story</label>
                <textarea
                  rows="4"
                  value={newMemory.content}
                  onChange={e => setNewMemory({ ...newMemory, content: e.target.value })}
                  placeholder="Details that AI can use later..."
                ></textarea>
              </div>
              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={newMemory.tags}
                  onChange={e => setNewMemory({ ...newMemory, tags: e.target.value })}
                  placeholder="bugfix, launch, celebration"
                />
              </div>
              <div className="form-group">
                <label>Visibility</label>
                <select
                  value={newMemory.visibility}
                  onChange={e => setNewMemory({ ...newMemory, visibility: e.target.value })}
                >
                  <option value="private">Private Note (Only me)</option>
                  <option value="shared">Mutual Win (Needs Approval)</option>
                </select>
              </div>

              {newMemory.visibility === 'shared' && (
                <div className="form-group">
                  <label>Recipient for "High-Five"</label>
                  <select
                    required
                    value={newMemory.recipient_id}
                    onChange={e => setNewMemory({ ...newMemory, recipient_id: e.target.value })}
                  >
                    <option value="">Select an employee...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
              )}

     
      

              <div className="modal__footer">
                <button type="button" className="btn btn--ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn--primary">Create Moment</button>
              </div>
            </form>
          </div>
        </div>
      )}

    {/* For editing and saving the exiting content */}
      {editingMemory && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal__header">
                    <h2>Edit Moment</h2>
                    <button onClick={() => setEditingMemory(null)}>×</button>
                  </div>
                  <form onSubmit={handleEditSave}>
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        required
                        value={editingMemory.title}
                        onChange={e => setEditingMemory({ ...editingMemory, title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>The Story</label>
                      <textarea
                        rows="4"
                        value={editingMemory.content}
                        onChange={e => setEditingMemory({ ...editingMemory, content: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tags (comma separated)</label>
                      <input
                        type="text"
                        value={editingMemory.tags}
                        onChange={e => setEditingMemory({ ...editingMemory, tags: e.target.value })}
                      />
                    </div>
                    <div className="modal__footer">
                      <button type="button" className="btn btn--ghost" onClick={() => setEditingMemory(null)}>Cancel</button>
                      <button type="submit" className="btn btn--primary">Save Changes</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
    </PageShell>
  );
};

export default Memories;
